import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Bloodtype as BloodSugarIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Notes as NotesIcon,
  Bloodtype as ValueIcon,
  List as ListIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useQueryClient, useQuery, useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { QK } from "../../../../../../base/qk";
import { useSnackbar } from "notistack";
import {
  getAssessmentByClientID,
  createAssessment,
  deleteAssessmentID,
} from "../../../../../../api/services/observations";
import CustomInput from "../../../../../CustomForm/CustomInput";
import CustomDateField from "../../../../../FormFields/CustomDateField";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import { useUserProfile } from "../../../../../../hooks/userProfile";
import DataTable from "../../../../../Table/DataTable";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import EditBloodSugarModal from "./editBloodSugar";
import CustomTimePicker from "../../../../../FormFields/TiimePicker/TimePicker";

const getNowDate = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const getNowTime = () => {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

const BloodSugar = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    value: "",
    notes: "",
    date_time: "",
    date: getNowDate(),
    time: getNowTime(),
    handover: false,
    manager: false,
  });
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { id: clientID } = useParams();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [RowID, setRowID] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedID, setSelectedID] = useState(null);

  const profile = useUserProfile();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckBox = (event) => {
    const { name, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const resetForm = () => {
    setFormData({
      value: "",
      notes: "",
      date_time: "",
      date: getNowDate(),
      time: getNowTime(),
      handover: false,
      manager: false,
    });
  };

  const { data: assessments, isLoading } = useQuery([QK.sugars, clientID], () =>
    getAssessmentByClientID(clientID, "sugar")
  );

  const createMutation = useMutation(createAssessment, {
    onSuccess: () => {
      enqueueSnackbar("Blood sugar entry created successfully.", {
        variant: "success",
      });
      queryClient.invalidateQueries([QK.sugars, clientID]);
      resetForm();
      setActiveTab(0);
    },
    onError: (error) => {
      enqueueSnackbar(error?.response?.data?.message || "Failed to create blood sugar entry.", {
        variant: "error",
      });
    },
  });

  const deleteMutation = useMutation(deleteAssessmentID, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.sugars, clientID]);
      enqueueSnackbar("Blood sugar entry deleted successfully", { variant: "success" });
      setShowConfirmModal(false);
    },
    onError: () => {
      enqueueSnackbar("Failed to delete blood sugar entry", { variant: "error" });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const combinedDateTime = formData.date && formData.time
      ? `${formData.date}T${formData.time}:00`
      : formData.date_time;

    const updatedFormData = {
      value: formData.value,
      notes: formData.notes,
      date_time: combinedDateTime,
      client: clientID,
      carer: profile?.id,
      assessment_type: "sugar",
      handover: formData.handover,
      manager: formData.manager,
    };
    createMutation.mutate(updatedFormData);
  };

  const handleOpenModal = (id) => {
    setSelectedID(id);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedID(null);
  };

  const handleUpdate = (id) => {
    handleOpenModal(id);
  };

  const handleDelete = (ID) => {
    setRowID(ID);
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    if (RowID) {
      deleteMutation.mutate(RowID);
    }
  };

  const headers = [
    { key: "value", label: "Value" },
    { key: "notes", label: "Notes" },
    { key: "date_time", label: "Date & Time" },
    { key: "manager", label: "Manager" },
    { key: "handover", label: "Handover" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "value") {
      return `${item.value || 0} mmol/L`;
    }
    if (header.key === "date_time") {
      return item.date_time ? new Date(item.date_time).toLocaleString() : "-";
    }
    if (header.key === "manager") {
      return item.manager ? (
        <Chip
          label="Yes"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 500,
            bgcolor: "#D1FAE5",
            color: "#039855",
          }}
        />
      ) : (
        <Chip
          label="No"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 500,
            bgcolor: "#FEE2E2",
            color: "#DC2626",
          }}
        />
      );
    }
    if (header.key === "handover") {
      return item.handover ? (
        <Chip
          label="Yes"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 500,
            bgcolor: "#D1FAE5",
            color: "#039855",
          }}
        />
      ) : (
        <Chip
          label="No"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 500,
            bgcolor: "#FEE2E2",
            color: "#DC2626",
          }}
        />
      );
    }
    if (header.key === "notes") {
      return (
        <span
          style={{
            display: "block",
            maxWidth: 150,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.notes || "-"}
        </span>
      );
    }
    return item[header.key] || "-";
  };

  const cardSx = {
    p: 1.75,
    borderRadius: "10px",
    border: "1px solid #EAECF0",
    bgcolor: "#FFFFFF",
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: "column",
    flex: "1 1 220px",
    minWidth: 0,
  };

  const fieldHeaderSx = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 1.25,
  };

  const iconBoxSx = (color) => ({
    width: 26,
    height: 26,
    borderRadius: "7px",
    bgcolor: alpha(color, 0.1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  });

  const labelSx = {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#344054",
  };

  const requiredBadgeSx = {
    fontSize: "0.65rem",
    fontWeight: 600,
    color: "#F04438",
    ml: "auto",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Card
        sx={{
          width: "100%",
          borderRadius: "14px",
          border: "1px solid #EAECF0",
          boxShadow: "0px 1px 3px rgba(16, 24, 40, 0.04)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            pt: 1.5,
            bgcolor: "#FFFFFF",
            borderBottom: "1px solid #EAECF0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "10px",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BloodSugarIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    color: "#101828",
                    letterSpacing: "-0.01em",
                  }}
                >
                  Blood Sugar
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "#667085", mt: 0.2 }}>
                  Manage blood sugar records
                </Typography>
              </Box>
            </Box>
            <Chip
              label={`${assessments?.length || 0} records`}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.65rem",
                fontWeight: 600,
                bgcolor: "#F2F4F7",
                color: "#344054",
                borderRadius: "6px",
                border: "1px solid #EAECF0",
              }}
            />
          </Box>

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 40,
              "& .MuiTabs-indicator": {
                backgroundColor: "#2469F0",
                height: 2.5,
                borderRadius: "3px 3px 0 0",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                minHeight: 40,
                color: "#667085",
                px: 2,
                "&.Mui-selected": {
                  color: "#2469F0",
                },
              },
            }}
          >
            <Tab
              icon={<ListIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Records"
              sx={{ gap: 0.5 }}
            />
            <Tab
              icon={<AddIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Add Record"
              sx={{ gap: 0.5 }}
            />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <CardContent sx={{ p: 0 }}>
            <DataTable
              data={assessments || []}
              headers={headers}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              canUpdate={true}
              canDelete={true}
              renderCell={renderCell}
              loading={isLoading}
            />
          </CardContent>
        )}

        {activeTab === 1 && (
          <CardContent sx={{ p: 3 }}>
            <form onSubmit={handleSubmit} id="blood-sugar-form" style={{ width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  width: "100%",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#EF4444", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#EF4444", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#EF4444")}>
                      <ValueIcon sx={{ color: "#EF4444", fontSize: 16 }} />
                    </Box>
                    <Typography sx={labelSx}>Blood Sugar</Typography>
                    <Typography sx={requiredBadgeSx}>Required</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomInput
                      name="value"
                      type="number"
                      value={formData.value || ""}
                      onChange={handleChange}
                      required
                      fullWidth
                      placeholder="Enter value (mmol/L)"
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#667085", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#667085", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#667085")}>
                      <CalendarIcon sx={{ color: "#667085", fontSize: 16 }} />
                    </Box>
                    <Typography sx={labelSx}>Date</Typography>
                    <Typography sx={requiredBadgeSx}>Required</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomDateField
                      name="date"
                      label=""
                      value={formData.date || ""}
                      onChange={handleChange}
                      required
                      fullWidth
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#8B5CF6", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#8B5CF6", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#8B5CF6")}>
                      <TimeIcon sx={{ color: "#8B5CF6", fontSize: 16 }} />
                    </Box>
                    <Typography sx={labelSx}>Time</Typography>
                    <Typography sx={requiredBadgeSx}>Required</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomTimePicker
                      name="time"
                      label=""
                      value={formData.time || ""}
                      onChange={handleChange}
                      required
                      fullWidth
                    />
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  ...cardSx,
                  flex: "1 1 100%",
                  mb: 2,
                  "&:hover": {
                    borderColor: alpha("#F59E0B", 0.4),
                    boxShadow: `0px 2px 8px ${alpha("#F59E0B", 0.08)}`,
                  },
                }}
              >
                <Box sx={fieldHeaderSx}>
                  <Box sx={iconBoxSx("#F59E0B")}>
                    <NotesIcon sx={{ color: "#F59E0B", fontSize: 16 }} />
                  </Box>
                  <Typography sx={labelSx}>Notes</Typography>
                </Box>
                <CustomTextArea
                  name="notes"
                  value={formData.notes || ""}
                  onChange={handleChange}
                  minRows={2}
                  maxRows={4}
                  fullWidth
                  placeholder="Add any notes..."
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 4,
                  flexWrap: "wrap",
                  p: 1.75,
                  borderRadius: "10px",
                  border: "1px solid #EAECF0",
                  bgcolor: "#FFFFFF",
                  mb: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      name="manager"
                      checked={formData.manager || false}
                      onChange={handleCheckBox}
                      size="small"
                      sx={{
                        color: "#D0D5DD",
                        "&.Mui-checked": { color: "#2469F0" },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "0.8rem", color: "#344054", fontWeight: 500 }}>
                      Share with Manager
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="handover"
                      checked={formData.handover || false}
                      onChange={handleCheckBox}
                      size="small"
                      sx={{
                        color: "#D0D5DD",
                        "&.Mui-checked": { color: "#2469F0" },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "0.8rem", color: "#344054", fontWeight: 500 }}>
                      Handover
                    </Typography>
                  }
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  pt: 2,
                  borderTop: "1px solid #EAECF0",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  disabled={createMutation.isLoading}
                  startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    borderRadius: "8px",
                    borderColor: "#D0D5DD",
                    color: "#344054",
                    px: 2.5,
                    height: 38,
                    "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
                  }}
                >
                  Clear
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={createMutation.isLoading}
                  startIcon={
                    createMutation.isLoading ? (
                      <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
                    ) : (
                      <SaveIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    borderRadius: "8px",
                    bgcolor: "#2469F0",
                    height: 38,
                    px: 3,
                    boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
                    "&:hover": {
                      bgcolor: "#1A5BD4",
                      boxShadow: "0px 2px 6px rgba(36, 105, 240, 0.25)",
                    },
                  }}
                >
                  {createMutation.isLoading ? "Saving..." : "Save Blood Sugar"}
                </Button>
              </Box>
            </form>
          </CardContent>
        )}
      </Card>

      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirm}
        title="Delete Confirmation"
        body="Are you sure you want to delete this blood sugar entry?"
        isDelete={true}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <EditBloodSugarModal
        show={showModal}
        handleClose={handleCloseModal}
        selectedID={selectedID}
      />
    </Box>
  );
};

export default BloodSugar;